// Smart alignment guides + snapping for Fabric v6.
// Adapted from the official fabric.js aligning_guidelines demo (MIT).
//
// While an object moves, its edges/center are compared against every other
// object's edges/center — including the workspace ("clip") rect, which gives
// page-edge and page-center snapping for free since it lives in the objects
// list. Within threshold, the moving object snaps and a magenta guide draws
// on the top (selection) context.
//
// HARD CONSTRAINT (verified against fabric 6.9.1 source): unlike v5,
// `toCanvasElement` no longer nulls `contextTop` during export — instead
// before:/after:render fire with the EXPORT context in `e.ctx` (and a
// temporarily swapped viewportTransform). Every draw/clear below MUST skip
// those export renders (e.ctx is neither contextContainer nor contextTop)
// or guides/badges paint the on-screen top canvas at export coordinates.
// Guides are never part of exports because handlers only ever draw on
// contextTop, and exports render to their own offscreen context.

import { ActiveSelection, Point } from 'fabric';

// True when a before:/after:render event belongs to the on-screen canvas
// (normal render → contextContainer; Canvas#renderTop → contextTop). False
// for toCanvasElement/toDataURL export renders, which pass their own ctx.
function isOnScreenRender(canvas, e) {
  const ctx = e?.ctx;
  if (!ctx) return true; // defensive: treat unknown as the v5 behavior
  return ctx === canvas.contextContainer || ctx === canvas.contextTop;
}

export function initAligningGuidelines(canvas, options = {}) {
  const aligningLineOffset = 5;
  const aligningLineMargin = options.margin ?? 8;
  const aligningLineColor = options.color || 'rgb(236, 72, 153)';
  const aligningLineWidth = 1;

  let enabled = options.enabled ?? true;
  let viewportTransform = null;
  let zoom = 1;

  const verticalLines = [];
  const horizontalLines = [];

  function drawLine(x1, y1, x2, y2) {
    // v6: getSelectionContext() still exists (alias of contextTop) but use
    // the documented property, consistent with the render handlers below.
    const ctx = canvas.contextTop;
    if (!ctx || !viewportTransform) return;
    ctx.save();
    ctx.lineWidth = aligningLineWidth;
    ctx.strokeStyle = aligningLineColor;
    ctx.beginPath();
    ctx.moveTo(x1 * zoom + viewportTransform[4], y1 * zoom + viewportTransform[5]);
    ctx.lineTo(x2 * zoom + viewportTransform[4], y2 * zoom + viewportTransform[5]);
    ctx.stroke();
    ctx.restore();
  }

  function drawVerticalLine(coords) {
    drawLine(
      coords.x + 0.5, Math.min(coords.y1, coords.y2),
      coords.x + 0.5, Math.max(coords.y1, coords.y2),
    );
  }

  function drawHorizontalLine(coords) {
    drawLine(
      Math.min(coords.x1, coords.x2), coords.y + 0.5,
      Math.max(coords.x1, coords.x2), coords.y + 0.5,
    );
  }

  function isInRange(value1, value2) {
    return Math.abs(value1 - value2) <= aligningLineMargin / zoom;
  }

  function onMouseDown() {
    viewportTransform = canvas.viewportTransform;
    zoom = canvas.getZoom();
  }

  function onObjectMoving(e) {
    if (!enabled) return;
    const activeObject = e.target;
    if (!activeObject || activeObject.name === 'clip') return;
    if (!viewportTransform) {
      viewportTransform = canvas.viewportTransform;
      zoom = canvas.getZoom();
    }

    // v6: object:moving always carries the live transform in the payload —
    // use it instead of the private canvas._currentTransform drag guard.
    if (!e.transform) return;

    // Objects inside a moving multi-selection must not be snap targets.
    // v6: the .type getter lowercases ('activeselection'), so the old
    // 'activeSelection' string compare silently failed — use instanceof.
    const selectionMembers =
      activeObject instanceof ActiveSelection ? activeObject.getObjects() : [];

    const canvasObjects = canvas.getObjects().filter((obj) =>
      obj !== activeObject
      && obj.visible !== false
      && !selectionMembers.includes(obj)
    );

    const activeObjectCenter = activeObject.getCenterPoint();
    const activeObjectLeft = activeObjectCenter.x;
    const activeObjectTop = activeObjectCenter.y;
    // v6: getBoundingRect() takes no args and returns SCENE-plane coords
    // (v5 default was viewport space, hence the old ÷viewportTransform;
    // keeping that division would make every width/height wrong by the
    // zoom factor — edge snaps and guide extents drift at any zoom ≠ 1).
    const activeObjectBoundingRect = activeObject.getBoundingRect();
    const activeObjectHeight = activeObjectBoundingRect.height;
    const activeObjectWidth = activeObjectBoundingRect.width;
    let horizontalInTheRange = false;
    let verticalInTheRange = false;

    for (let i = canvasObjects.length; i--;) {
      const obj = canvasObjects[i];
      const objectCenter = obj.getCenterPoint();
      const objectLeft = objectCenter.x;
      const objectTop = objectCenter.y;
      const objectBoundingRect = obj.getBoundingRect(); // scene space in v6
      const objectHeight = objectBoundingRect.height;
      const objectWidth = objectBoundingRect.width;

      // snap by the vertical center line
      if (isInRange(objectLeft, activeObjectLeft)) {
        verticalInTheRange = true;
        verticalLines.push({
          x: objectLeft,
          y1: (objectTop < activeObjectTop)
            ? (objectTop - objectHeight / 2 - aligningLineOffset)
            : (objectTop + objectHeight / 2 + aligningLineOffset),
          y2: (activeObjectTop > objectTop)
            ? (activeObjectTop + activeObjectHeight / 2 + aligningLineOffset)
            : (activeObjectTop - activeObjectHeight / 2 - aligningLineOffset),
        });
        activeObject.setPositionByOrigin(new Point(objectLeft, activeObjectTop), 'center', 'center');
      }

      // snap by the left edge
      if (isInRange(objectLeft - objectWidth / 2, activeObjectLeft - activeObjectWidth / 2)) {
        verticalInTheRange = true;
        verticalLines.push({
          x: objectLeft - objectWidth / 2,
          y1: (objectTop < activeObjectTop)
            ? (objectTop - objectHeight / 2 - aligningLineOffset)
            : (objectTop + objectHeight / 2 + aligningLineOffset),
          y2: (activeObjectTop > objectTop)
            ? (activeObjectTop + activeObjectHeight / 2 + aligningLineOffset)
            : (activeObjectTop - activeObjectHeight / 2 - aligningLineOffset),
        });
        activeObject.setPositionByOrigin(
          new Point(objectLeft - objectWidth / 2 + activeObjectWidth / 2, activeObjectTop),
          'center', 'center',
        );
      }

      // snap by the right edge
      if (isInRange(objectLeft + objectWidth / 2, activeObjectLeft + activeObjectWidth / 2)) {
        verticalInTheRange = true;
        verticalLines.push({
          x: objectLeft + objectWidth / 2,
          y1: (objectTop < activeObjectTop)
            ? (objectTop - objectHeight / 2 - aligningLineOffset)
            : (objectTop + objectHeight / 2 + aligningLineOffset),
          y2: (activeObjectTop > objectTop)
            ? (activeObjectTop + activeObjectHeight / 2 + aligningLineOffset)
            : (activeObjectTop - activeObjectHeight / 2 - aligningLineOffset),
        });
        activeObject.setPositionByOrigin(
          new Point(objectLeft + objectWidth / 2 - activeObjectWidth / 2, activeObjectTop),
          'center', 'center',
        );
      }

      // snap by the horizontal center line
      if (isInRange(objectTop, activeObjectTop)) {
        horizontalInTheRange = true;
        horizontalLines.push({
          y: objectTop,
          x1: (objectLeft < activeObjectLeft)
            ? (objectLeft - objectWidth / 2 - aligningLineOffset)
            : (objectLeft + objectWidth / 2 + aligningLineOffset),
          x2: (activeObjectLeft > objectLeft)
            ? (activeObjectLeft + activeObjectWidth / 2 + aligningLineOffset)
            : (activeObjectLeft - activeObjectWidth / 2 - aligningLineOffset),
        });
        activeObject.setPositionByOrigin(new Point(activeObjectLeft, objectTop), 'center', 'center');
      }

      // snap by the top edge
      if (isInRange(objectTop - objectHeight / 2, activeObjectTop - activeObjectHeight / 2)) {
        horizontalInTheRange = true;
        horizontalLines.push({
          y: objectTop - objectHeight / 2,
          x1: (objectLeft < activeObjectLeft)
            ? (objectLeft - objectWidth / 2 - aligningLineOffset)
            : (objectLeft + objectWidth / 2 + aligningLineOffset),
          x2: (activeObjectLeft > objectLeft)
            ? (activeObjectLeft + activeObjectWidth / 2 + aligningLineOffset)
            : (activeObjectLeft - activeObjectWidth / 2 - aligningLineOffset),
        });
        activeObject.setPositionByOrigin(
          new Point(activeObjectLeft, objectTop - objectHeight / 2 + activeObjectHeight / 2),
          'center', 'center',
        );
      }

      // snap by the bottom edge
      if (isInRange(objectTop + objectHeight / 2, activeObjectTop + activeObjectHeight / 2)) {
        horizontalInTheRange = true;
        horizontalLines.push({
          y: objectTop + objectHeight / 2,
          x1: (objectLeft < activeObjectLeft)
            ? (objectLeft - objectWidth / 2 - aligningLineOffset)
            : (objectLeft + objectWidth / 2 + aligningLineOffset),
          x2: (activeObjectLeft > objectLeft)
            ? (activeObjectLeft + activeObjectWidth / 2 + aligningLineOffset)
            : (activeObjectLeft - activeObjectWidth / 2 - aligningLineOffset),
        });
        activeObject.setPositionByOrigin(
          new Point(activeObjectLeft, objectTop + objectHeight / 2 - activeObjectHeight / 2),
          'center', 'center',
        );
      }
    }

    if (!horizontalInTheRange) horizontalLines.length = 0;
    if (!verticalInTheRange) verticalLines.length = 0;

    // ---- RULER GUIDES (RS2 lane) — user-dragged guides join the snap ----
    // Model: workspace.data.guides = [{axis:'h'|'v', pos}], pos in canvas
    // units from the POSTER's top-left. ADDITIVE pass: runs after the object
    // pass with its own tighter threshold (6 SCREEN px ÷ zoom → canvas
    // units), candidates = the moving box's left/center/right vs vertical
    // guides and top/center/bottom vs horizontal ones, nearest candidate
    // wins per axis. Running last means a guide beats an object snap on the
    // same axis when both are in range — guides are deliberate user marks.
    // Same `enabled` flag (the footer Snap toggle), MOVE only (this handler
    // is object:moving). Visual feedback: the guide's own DOM hairline
    // brightens (CanvasViewport listens for cce-guide-snap) instead of a
    // duplicate magenta line drawn at the same x/y.
    const wsClip = canvas.getObjects().find((o) => o.name === 'clip');
    const rulerGuides = Array.isArray(wsClip?.data?.guides) ? wsClip.data.guides : [];
    if (rulerGuides.length) {
      const wsBr = wsClip.getBoundingRect(); // scene space in v6
      const guideMargin = 6 / zoom;
      const center = activeObject.getCenterPoint(); // AFTER the object pass
      const halfW = activeObjectWidth / 2;
      const halfH = activeObjectHeight / 2;
      let bestV = null;
      let bestH = null;
      rulerGuides.forEach((g, index) => {
        if (!g || !Number.isFinite(g.pos)) return;
        if (g.axis === 'v') {
          const x = wsBr.left + g.pos;
          const candidates = [
            { d: Math.abs(center.x - halfW - x), cx: x + halfW }, // left edge
            { d: Math.abs(center.x - x), cx: x },                 // center
            { d: Math.abs(center.x + halfW - x), cx: x - halfW }, // right edge
          ];
          for (const c of candidates) {
            if (c.d <= guideMargin && (!bestV || c.d < bestV.d)) bestV = { d: c.d, cx: c.cx, index };
          }
        } else if (g.axis === 'h') {
          const y = wsBr.top + g.pos;
          const candidates = [
            { d: Math.abs(center.y - halfH - y), cy: y + halfH }, // top edge
            { d: Math.abs(center.y - y), cy: y },                 // center
            { d: Math.abs(center.y + halfH - y), cy: y - halfH }, // bottom edge
          ];
          for (const c of candidates) {
            if (c.d <= guideMargin && (!bestH || c.d < bestH.d)) bestH = { d: c.d, cy: c.cy, index };
          }
        }
      });
      if (bestV || bestH) {
        activeObject.setPositionByOrigin(
          new Point(bestV ? bestV.cx : center.x, bestH ? bestH.cy : center.y),
          'center', 'center',
        );
        if (bestV) window.dispatchEvent(new CustomEvent('cce-guide-snap', { detail: { axis: 'v', index: bestV.index } }));
        if (bestH) window.dispatchEvent(new CustomEvent('cce-guide-snap', { detail: { axis: 'h', index: bestH.index } }));
      }
    }
  }

  function onBeforeRender(e) {
    // Skip export renders (see header): don't clear the on-screen top
    // context while toCanvasElement renders to its own offscreen ctx.
    if (!isOnScreenRender(canvas, e)) return;
    const ctx = canvas.contextTop;
    if (ctx) canvas.clearContext(ctx);
  }

  function onAfterRender(e) {
    if (!isOnScreenRender(canvas, e)) return;
    if (!canvas.contextTop) return;
    for (let i = verticalLines.length; i--;) drawVerticalLine(verticalLines[i]);
    for (let i = horizontalLines.length; i--;) drawHorizontalLine(horizontalLines[i]);
    // The arrays refill on every object:moving tick while guides still apply
    verticalLines.length = 0;
    horizontalLines.length = 0;
  }

  function onMouseUp() {
    verticalLines.length = 0;
    horizontalLines.length = 0;
    canvas.renderAll();
  }

  canvas.on('mouse:down', onMouseDown);
  canvas.on('object:moving', onObjectMoving);
  canvas.on('before:render', onBeforeRender);
  canvas.on('after:render', onAfterRender);
  canvas.on('mouse:up', onMouseUp);

  return {
    setEnabled: (value) => { enabled = !!value; },
    isEnabled: () => enabled,
    dispose: () => {
      canvas.off('mouse:down', onMouseDown);
      canvas.off('object:moving', onObjectMoving);
      canvas.off('before:render', onBeforeRender);
      canvas.off('after:render', onAfterRender);
      canvas.off('mouse:up', onMouseUp);
    },
  };
}

// Lock badge overlay: draws a small padlock at the top-right of every locked
// object. Drawn on contextTop (same export-render skip rule as the guides) —
// export-safe because exports render to their own offscreen context and the
// handler bails on those renders, so badges can never leak into saved PNGs
// or print output.
export function initLockBadges(canvas) {
  function drawBadges(e) {
    if (!isOnScreenRender(canvas, e)) return;
    const ctx = canvas.contextTop;
    if (!ctx) return;
    const lockedObjects = canvas.getObjects().filter(
      (o) => o.locked === true && o.name !== 'clip' && o.visible !== false,
    );
    if (lockedObjects.length === 0) return;
    ctx.save();
    ctx.font = '13px sans-serif';
    ctx.textBaseline = 'top';
    const vpt = canvas.viewportTransform;
    for (const obj of lockedObjects) {
      // v6: getBoundingRect() returns SCENE-plane coords (v5's no-arg call
      // was screen space) — project through the viewportTransform so badges
      // stay glued to objects at any zoom/pan. Badge size stays in screen px.
      const br = obj.getBoundingRect();
      const left = br.left * vpt[0] + vpt[4];
      const top = br.top * vpt[3] + vpt[5];
      const width = br.width * vpt[0];
      ctx.fillText('🔒', left + width - 18, top + 3);
    }
    ctx.restore();
  }

  canvas.on('after:render', drawBadges);
  return () => canvas.off('after:render', drawBadges);
}
